import React, { useState, useEffect } from "react";
import { X, Trash2, Plus, ShoppingCart, User, Phone } from "lucide-react";
import ProductCatalogModal from "./ProductCatalogModal";
import SearchableSelect from "./SearchableSelect";
import { masterService } from "../api/masterService";
import { useNotification } from "../contexts/NotificationContext";
import {
  formatNumberWithSeparator,
  parseNumberFromSeparator,
} from "../utils/numberFormat";

export default function AddProcurementModal({
  isOpen,
  onClose,
  onSubmit,
  editData = null,
}) {
  const { showSuccess, showError } = useNotification();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const [supplier, setSupplier] = useState("");
  const [requestDate, setRequestDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [picSupplier, setPicSupplier] = useState("");
  const [supplierPhone, setSupplierPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [discount, setDiscount] = useState(0);
  const [additionalFees, setAdditionalFees] = useState(0);

  // Data State
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [assetUnits, setAssetUnits] = useState([]);
  const [loadingAssetUnits, setLoadingAssetUnits] = useState(false);

  const getCurrentUser = () => {
    try {
      const uData = JSON.parse(
        localStorage.getItem("user_data") ||
          localStorage.getItem("user") ||
          "{}",
      );
      return (
        uData?.full_name ||
        uData?.name ||
        uData?.username ||
        localStorage.getItem("username") ||
        localStorage.getItem("name") ||
        "Admin"
      );
    } catch {
      return (
        localStorage.getItem("username") ||
        localStorage.getItem("name") ||
        "Admin"
      );
    }
  };
  const creatorName = getCurrentUser();

  // Fetch suppliers, asset units, and initialize form data
  useEffect(() => {
    if (isOpen) {
      fetchSuppliers();
      fetchAssetUnits();
      if (editData) {
        setSupplier(editData.supplier_id || editData.supplier?.id || "");

        // Parse public notes, internal notes, pic, phone
        const rawNotes = editData.notes || "";
        const picMatch = rawNotes.match(
          /\[PIC:\s*([^\]\-]+)(?:\s*-\s*([^\]]+))?\]/,
        );
        const internalMatch = rawNotes.match(/\[Catatan Internal:\s*([^\]]+)\]/);
        const cleanPubNotes = rawNotes
          .replace(/\[PIC:[^\]]+\]/g, "")
          .replace(/\[Catatan Internal:[^\]]+\]/g, "")
          .split("|")
          .map((s) => s.trim())
          .filter(Boolean)
          .join(" | ");

        setPicSupplier(
          editData.supplier_pic ||
            editData.pic_supplier ||
            editData.pic_name ||
            editData.supplier?.contact_name ||
            editData.supplier?.pic_name ||
            (picMatch ? picMatch[1]?.trim() : "") ||
            "",
        );
        setSupplierPhone(
          editData.supplier_phone ||
            editData.supplier?.phone_number ||
            editData.supplier?.phone ||
            (picMatch ? picMatch[2]?.trim() : "") ||
            "",
        );
        setNotes(cleanPubNotes);
        setInternalNotes(internalMatch ? internalMatch[1]?.trim() : "");

        const reqDateStr = editData.request_date
          ? new Date(editData.request_date).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0];
        const dueDateStr = editData.due_date
          ? new Date(editData.due_date).toISOString().split("T")[0]
          : new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
              .toISOString()
              .split("T")[0];

        setRequestDate(reqDateStr);
        setDueDate(dueDateStr);
        setDiscount(editData.discount_amount || 0);
        setAdditionalFees(editData.additional_fee || 0);

        // Map items
        const mappedItems = (editData.items || []).map((item) => {
          const itemKey = `${item.product_id || item.id}-${item.variant_id || "base"}`;
          const qty = Number(item.qty_ordered || item.quantity || 1);
          const price = Number(
            item.unit_price_estimated || item.unit_price || 0,
          );
          const brandName = item.brand_name || item.brand || item.variant_name || "";
          const physicalUnit = item.product_unit_name || item.satuan_name || item.satuan || "Pcs";
          return {
            id: itemKey,
            product_id: item.product_id || item.id,
            variant_id: item.variant_id ?? null,
            unit_id: item.unit_id || item.unit?.id || null,
            name: item.product_name || item.name || "-",
            variant_name: item.variant_name || null,
            brand_name: brandName,
            product_unit_name: physicalUnit,
            unit_name: physicalUnit,
            product_code: item.sku || item.product_code || item.code || "-",
            quantity: qty,
            price: price,
            subtotal: qty * price,
          };
        });
        setSelectedProducts(mappedItems);
      } else {
        // Reset form
        setSelectedProducts([]);
        setSupplier("");
        setPicSupplier("");
        setSupplierPhone("");
        setDiscount(0);
        setAdditionalFees(0);
        const today = new Date().toISOString().split("T")[0];
        const defaultDueDate = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0];
        setRequestDate(today);
        setDueDate(defaultDueDate);
        setNotes("");
        setInternalNotes("");
      }
    }
  }, [isOpen, editData]);

  const fetchSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      const response = await masterService.suppliers.list({ status: "ACTIVE" });
      setSuppliers(response.data || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const fetchAssetUnits = async () => {
    try {
      setLoadingAssetUnits(true);
      const response = await masterService.assetUnits.list({ limit: 500, status: "ACTIVE" });
      const list = response?.data || response?.items || (Array.isArray(response) ? response : []);
      setAssetUnits(list);
    } catch (error) {
      console.error("Error fetching asset units:", error);
    } finally {
      setLoadingAssetUnits(false);
    }
  };

  const handleAddSupplier = async (name) => {
    try {
      const payload = {
        name: name.trim(),
      };
      if (supplierPhone && supplierPhone.trim()) {
        payload.phone_number = supplierPhone.trim();
      }
      if (picSupplier && picSupplier.trim()) {
        payload.contact_name = picSupplier.trim();
      }
      const res = await masterService.suppliers.create(payload);
      let newSup = res.data?.data || res.data;

      if (!newSup || !newSup.id) {
        const fetchRes = await masterService.suppliers.list({
          status: "ACTIVE",
          query: name.trim(),
        });
        const list = fetchRes.data || [];
        newSup =
          list.find(
            (s) => s.name?.toLowerCase() === name.trim().toLowerCase(),
          ) || list[0];
      }

      if (newSup && newSup.id) {
        setSuppliers((prev) => [newSup, ...prev.filter((s) => s.id !== newSup.id)]);
        setSupplier(newSup.id);
        if (newSup.contact_name || newSup.pic_name) {
          setPicSupplier(newSup.contact_name || newSup.pic_name || "");
        }
        if (newSup.phone_number || newSup.phone) {
          setSupplierPhone(newSup.phone_number || newSup.phone || "");
        }
        showSuccess(`Supplier "${newSup.name}" berhasil ditambahkan`);
        return {
          id: newSup.id,
          name: `${newSup.name}${newSup.contact_name ? ` (${newSup.contact_name})` : ""}`,
        };
      }
    } catch (err) {
      showError(err.message || "Gagal menambah supplier");
      throw err;
    }
  };

  const handleSupplierChange = (supplierId) => {
    setSupplier(supplierId);
    if (!supplierId) {
      setPicSupplier("");
      setSupplierPhone("");
      return;
    }
    const selected = suppliers.find((s) => s.id === supplierId);
    if (selected) {
      setPicSupplier(selected.contact_name || selected.pic_name || "");
      setSupplierPhone(selected.phone_number || selected.phone || "");
    }
  };

  const handleAddProducts = (products) => {
    // Merge new products, avoiding duplicates
    const newProducts = products
      .filter((p) => {
        const pKey = `${p.product_id || p.id}-${p.variant_id || "base"}`;
        return !selectedProducts.some((sp) => {
          const spKey = `${sp.product_id || sp.id}-${sp.variant_id || "base"}`;
          return spKey === pKey;
        });
      })
      .map((p) => {
        const itemKey = `${p.product_id || p.id}-${p.variant_id || "base"}`;
        const rawPrice =
          p.purchase_price ??
          p.buy_price ??
          p.buyPrice ??
          p.cost_price ??
          p.last_buy_price ??
          0;
        const price = Number(rawPrice);
        const brandName = p.brand_name || p.brand?.name || p.brand || p.variant_name || "";
        const physicalUnit = p.product_unit_name || p.satuan_name || p.satuan || p.unit?.name || p.unit_name || "Pcs";
        return {
          ...p,
          id: itemKey,
          product_id: p.product_id || p.id,
          variant_id: p.variant_id || null,
          unit_id: p.target_unit_id || p.unit_id || null,
          product_code: p.product_code || p.code || p.sku || "-",
          name: p.product_name || p.name || "-",
          variant_name: p.variant_name || null,
          brand_name: brandName,
          product_unit_name: physicalUnit,
          unit_name: physicalUnit,
          price: price,
          quantity: 1,
          subtotal: price,
        };
      });

    setSelectedProducts([...selectedProducts, ...newProducts]);
  };

  const handleRemoveProduct = (id) => {
    setSelectedProducts(selectedProducts.filter((p) => p.id !== id));
  };

  const updateProduct = (id, field, value) => {
    setSelectedProducts(
      selectedProducts.map((p) => {
        if (p.id === id) {
          const updated = { ...p, [field]: value };
          if (field === "quantity" || field === "price") {
            const q = field === "quantity" ? Number(value || 0) : Number(p.quantity || 0);
            const pr = field === "price" ? Number(value || 0) : Number(p.price || 0);
            updated.subtotal = q * pr;
          }
          return updated;
        }
        return p;
      }),
    );
  };

  const subtotal = selectedProducts.reduce((sum, p) => sum + (p.subtotal || 0), 0);
  const totalBelanja = Math.max(0, subtotal - Number(discount || 0) + Number(additionalFees || 0));

  const formatCurrency = (val) => {
    if (val === undefined || val === null || isNaN(val)) return "Rp 0";
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(val);
  };

  const handleSubmit = async (submitStatus = "APPROVED") => {
    if (!supplier) {
      alert("Mohon pilih supplier terlebih dahulu");
      return;
    }
    if (selectedProducts.length === 0) {
      alert("Mohon tambahkan minimal satu produk ke daftar pesanan");
      return;
    }
    if (!requestDate) {
      alert("Mohon isi tanggal order");
      return;
    }
    if (!dueDate) {
      alert("Mohon isi estimasi tanggal tiba");
      return;
    }

    const invalidProducts = selectedProducts.filter((p) => !p.quantity || Number(p.quantity) <= 0);
    if (invalidProducts.length > 0) {
      alert("Mohon isi jumlah (Qty) yang valid untuk semua produk");
      return;
    }

    setSubmitting(true);
    try {
      const mergedNotes = [
        notes?.trim(),
        internalNotes?.trim() ? `[Catatan Internal: ${internalNotes.trim()}]` : "",
        picSupplier ? `[PIC: ${picSupplier}${supplierPhone ? ` - ${supplierPhone}` : ""}]` : "",
      ].filter(Boolean).join(" | ");

      const payload = {
        supplier_id: supplier,
        request_date: requestDate,
        due_date: dueDate,
        notes: mergedNotes || notes || undefined,
        status: submitStatus,
        total_estimated_amount: totalBelanja,
        items: selectedProducts.map((p) => ({
          product_id: p.product_id || p.id,
          variant_id: p.variant_id ?? null,
          unit_id: p.unit_id || null,
          qty_ordered: Number(p.quantity || 0),
          unit_price_estimated: Number(p.price || 0),
          total_price_estimated: Number(p.subtotal || 0),
        })),
      };

      await onSubmit(payload, editData?.id);
      onClose();
    } catch (error) {
      console.error("Error submitting purchase order:", error);
      alert(error?.message || "Gagal membuat Purchase Order");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 modal-backdrop-enter">
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-2xl w-full max-w-5xl flex flex-col max-h-[92vh] overflow-hidden modal-panel-enter">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-teal-600 text-white rounded-t-2xl">
            <div>
              <h2 className="text-base font-bold tracking-tight flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-teal-200" />
                <span>{editData ? "Edit Purchase Order" : "Buat Purchase Order"}</span>
              </h2>
              <p className="text-xs text-teal-100 font-normal mt-0.5">
                Tambahkan informasi supplier dan bahan / produk yang akan dibeli
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form Content - Scrollable */}
          <div className="p-6 overflow-y-auto space-y-6 flex-1">
            {/* Top Row: Supplier & Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Supplier Selection */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Supplier <span className="text-rose-500">*</span>
                </label>
                <SearchableSelect
                  options={suppliers.map((s) => ({
                    id: s.id,
                    name: `${s.name}${s.contact_name ? ` (${s.contact_name})` : ""}`,
                  }))}
                  value={supplier}
                  onChange={handleSupplierChange}
                  onAddNew={handleAddSupplier}
                  addNewLabel="Tambah Supplier Baru"
                  maxOptionsHeight="max-h-48"
                  fetchOptions={async (term) => {
                    const response = await masterService.suppliers.list({
                      status: "ACTIVE",
                      query: term,
                    });
                    const list = response.data || [];
                    return list.map((s) => ({
                      id: s.id,
                      name: `${s.name}${s.contact_name ? ` (${s.contact_name})` : ""}`,
                    }));
                  }}
                  minSearchLength={1}
                  placeholder={loadingSuppliers ? "Memuat supplier..." : "Pilih Supplier..."}
                  disabled={loadingSuppliers}
                  displayKey="name"
                  valueKey="id"
                  className="w-full"
                />
              </div>

              {/* Dibuat Oleh */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Dibuat Oleh
                </label>
                <div className="flex items-center gap-2 px-3 py-2 bg-white ring-1 ring-slate-200 rounded-lg text-sm text-slate-700 font-medium">
                  <User className="w-4 h-4 text-slate-400" />
                  <span>{creatorName}</span>
                </div>
              </div>

              {/* Tanggal Order */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Tanggal Order <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={requestDate}
                    onChange={(e) => setRequestDate(e.target.value)}
                    className="w-full px-3 py-2 bg-white ring-1 ring-slate-200 border-0 rounded-lg text-sm text-slate-800 focus:ring-2 focus:ring-teal-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Tanggal Estimasi */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Tanggal Estimasi Tiba <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={dueDate}
                    min={requestDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full px-3 py-2 bg-white ring-1 ring-slate-200 border-0 rounded-lg text-sm text-slate-800 focus:ring-2 focus:ring-teal-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* PIC Supplier */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  PIC Supplier
                </label>
                <input
                  type="text"
                  placeholder="Nama PIC (Opsional)"
                  value={picSupplier}
                  onChange={(e) => setPicSupplier(e.target.value)}
                  className="w-full px-3 py-2 bg-white ring-1 ring-slate-200 border-0 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>

              {/* No Telepon Supplier */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  No. Telepon PIC
                </label>
                <div className="flex items-center gap-1.5 px-3 py-2 bg-white ring-1 ring-slate-200 rounded-lg text-sm text-slate-800">
                  <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <input
                    type="tel"
                    inputMode="tel"
                    placeholder="08xxxxxxxxxx"
                    value={supplierPhone}
                    onChange={(e) => setSupplierPhone(e.target.value)}
                    className="w-full bg-transparent border-0 p-0 focus:outline-none text-sm text-slate-800"
                  />
                </div>
              </div>

              {/* Catatan PO */}
              <div className="lg:col-span-2">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Catatan PO
                </label>
                <input
                  type="text"
                  placeholder="Tambahkan catatan PO ke supplier (opsional)..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 bg-white ring-1 ring-slate-200 border-0 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>
            </div>

            {/* Product Table Header & Actions */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900 tracking-tight flex items-center gap-2">
                  <span>Daftar Produk / Bahan yang Dipesan</span>
                  <span className="bg-teal-50 text-teal-700 text-xs px-2 py-0.5 rounded-full font-semibold ring-1 ring-teal-200">
                    {selectedProducts.length} Item
                  </span>
                </h3>
                <button
                  type="button"
                  onClick={() => setIsCatalogOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-semibold text-xs transition shadow-sm shadow-teal-600/20">
                  <Plus className="w-4 h-4" />
                  <span>Tambah Produk</span>
                </button>
              </div>

              {/* Table Card */}
              <div className="bg-white rounded-xl ring-1 ring-slate-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-3 py-2.5 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap w-10">
                          No
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap min-w-[170px]">
                          Nama Produk
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap w-28">
                          Merek
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap w-40">
                          Target Unit / Mesin
                        </th>
                        <th className="px-3 py-2.5 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap w-20">
                          Satuan
                        </th>
                        <th className="px-3 py-2.5 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap w-24">
                          Qty
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap w-32">
                          Harga Satuan
                        </th>
                        <th className="px-3 py-2.5 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap w-32">
                          Subtotal
                        </th>
                        <th className="px-3 py-2.5 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap w-12">
                          Aksi
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {selectedProducts.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="px-6 py-12 text-center text-slate-400 text-sm">
                            <div className="flex flex-col items-center justify-center gap-1">
                              <p className="font-medium text-slate-600">Belum ada produk yang ditambahkan</p>
                              <p className="text-xs text-slate-400">Klik tombol "+ Tambah Produk" di atas untuk memilih produk dari katalog</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        selectedProducts.map((product, index) => {
                          const brandDisplay = product.brand_name || product.brand || product.variant_name || "-";
                          const isVariantDistinctFromBrand = product.variant_name && product.variant_name !== brandDisplay;

                          return (
                            <tr key={product.id} className="hover:bg-slate-50/70 transition-colors">
                              {/* No */}
                              <td className="px-3 py-2.5 text-center text-xs text-slate-500 font-medium">
                                {index + 1}
                              </td>

                              {/* Nama Produk */}
                              <td className="px-3 py-2.5">
                                <div className="text-xs font-bold text-slate-900">
                                  {product.name}
                                </div>
                                {isVariantDistinctFromBrand && (
                                  <span className="text-[11px] text-slate-500">
                                    Varian: {product.variant_name}
                                  </span>
                                )}
                              </td>

                              {/* Merek */}
                              <td className="px-3 py-2.5 text-xs font-semibold text-slate-800">
                                {brandDisplay}
                              </td>

                              {/* Target Unit Dropdown */}
                              <td className="px-3 py-2.5">
                                <select
                                  value={product.unit_id || ""}
                                  onChange={(e) => updateProduct(product.id, "unit_id", e.target.value || null)}
                                  className="w-full min-w-[130px] px-2 py-1.5 text-xs text-slate-800 bg-white ring-1 ring-slate-300 border-0 rounded-lg focus:ring-2 focus:ring-teal-500 focus:outline-none shadow-2xs font-medium truncate">
                                  <option value="">Stok Gudang (Umum)</option>
                                  {assetUnits.map((u) => (
                                    <option key={u.id} value={u.id}>
                                      {u.name} {u.jenis ? `(${u.jenis})` : ""}
                                    </option>
                                  ))}
                                </select>
                              </td>

                              {/* Satuan Fisik */}
                              <td className="px-3 py-2.5 text-center text-xs font-medium text-slate-700">
                                {product.product_unit_name || product.satuan_name || product.satuan || product.unit_name || "Pcs"}
                              </td>

                              {/* Qty Input */}
                              <td className="px-3 py-2.5 text-center">
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={formatNumberWithSeparator(product.quantity)}
                                  onChange={(e) => updateProduct(product.id, "quantity", parseNumberFromSeparator(e.target.value))}
                                  className="w-20 px-2.5 py-1.5 text-center font-bold text-slate-900 bg-white ring-1 ring-slate-300 border-0 rounded-lg text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none shadow-2xs"
                                />
                              </td>

                              {/* Harga Satuan Input */}
                              <td className="px-3 py-2.5 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <span className="text-[11px] text-slate-400 font-medium">Rp</span>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    value={formatNumberWithSeparator(product.price)}
                                    onChange={(e) => updateProduct(product.id, "price", parseNumberFromSeparator(e.target.value))}
                                    className="w-28 px-2.5 py-1.5 text-right font-semibold text-slate-900 bg-white ring-1 ring-slate-300 border-0 rounded-lg text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none shadow-2xs"
                                  />
                                </div>
                              </td>

                              {/* Subtotal */}
                              <td className="px-3 py-2.5 text-right font-bold text-xs text-slate-900">
                                {formatCurrency(product.subtotal)}
                              </td>

                              {/* Hapus Button */}
                              <td className="px-3 py-2.5 text-center">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveProduct(product.id)}
                                  className="w-7 h-7 flex items-center justify-center bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-lg transition">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Bottom Summary & Notes Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              {/* Catatan Internal */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Catatan Internal (Opsional)
                </label>
                <textarea
                  rows="4"
                  placeholder="Catatan khusus untuk tim internal..."
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 ring-1 ring-slate-200 border-0 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:bg-white resize-none"
                />
              </div>

              {/* Financial Calculation Box */}
              <div className="bg-slate-50/90 p-4 rounded-xl ring-1 ring-slate-200/80 space-y-3 text-xs">
                <div className="flex items-center justify-between text-slate-600">
                  <span className="font-medium">Subtotal Item</span>
                  <span className="font-bold text-slate-900">{formatCurrency(subtotal)}</span>
                </div>

                <div className="flex items-center justify-between text-slate-600">
                  <span className="font-medium">Diskon</span>
                  <div className="flex items-center gap-1.5 w-40 justify-end">
                    <span className="text-slate-400 font-medium text-xs">Rp</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatNumberWithSeparator(discount)}
                      onChange={(e) => setDiscount(parseNumberFromSeparator(e.target.value))}
                      placeholder="0"
                      className="w-32 px-2.5 py-1.5 text-right font-semibold text-slate-900 bg-white ring-1 ring-slate-300 border-0 rounded-lg text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none shadow-2xs"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between text-slate-600">
                  <span className="font-medium">Biaya Tambahan / Ongkir</span>
                  <div className="flex items-center gap-1.5 w-40 justify-end">
                    <span className="text-slate-400 font-medium text-xs">Rp</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatNumberWithSeparator(additionalFees)}
                      onChange={(e) => setAdditionalFees(parseNumberFromSeparator(e.target.value))}
                      placeholder="0"
                      className="w-32 px-2.5 py-1.5 text-right font-semibold text-slate-900 bg-white ring-1 ring-slate-300 border-0 rounded-lg text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none shadow-2xs"
                    />
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-200 flex items-center justify-between text-sm font-bold text-slate-900">
                  <span>Total Nilai PO</span>
                  <span className="text-teal-700 font-extrabold text-base">{formatCurrency(totalBelanja)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
            <span className="text-xs text-slate-500">
              <span className="text-rose-500">*</span> Field wajib diisi
            </span>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg hover:bg-slate-100 font-semibold text-xs transition disabled:opacity-50">
                Batal
              </button>
              {editData ? (
                <button
                  type="button"
                  onClick={() => handleSubmit(editData.status || "SUBMITTED")}
                  disabled={submitting || selectedProducts.length === 0 || !supplier}
                  className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-semibold text-xs shadow-sm shadow-teal-600/20 transition disabled:opacity-50 disabled:cursor-not-allowed">
                  {submitting ? "Menyimpan..." : "Simpan Perubahan"}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => handleSubmit("DRAFT")}
                    disabled={submitting || selectedProducts.length === 0 || !supplier}
                    className="px-4 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300 font-semibold text-xs transition disabled:opacity-50 disabled:cursor-not-allowed">
                    Simpan Draft
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSubmit("APPROVED")}
                    disabled={submitting || selectedProducts.length === 0 || !supplier}
                    className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-semibold text-xs shadow-sm shadow-teal-600/20 transition disabled:opacity-50 disabled:cursor-not-allowed">
                    {submitting ? "Memproses..." : "Buat Purchase Order"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <ProductCatalogModal
        isOpen={isCatalogOpen}
        onClose={() => setIsCatalogOpen(false)}
        onSelect={handleAddProducts}
        dataSource="inventory"
      />
    </>
  );
}
